/**
 * The events year: a timeline of our programs, the conferences we track, the
 * portfolio's travel to them, and holidays, one row each, grouped. Built from
 * the program series and the event and deadline times the caller read for a
 * range; every interface draws the same rows.
 */

import { toDate } from "./dates.js"
import { HOSTED_EVENT_SCHEMAS } from "./hosted.js"
import { programActive } from "./programs.js"

export const CONFERENCE_SCHEMA = "Community Event"
export const TRAVEL_SCHEMA = "Travel"
export const HOLIDAY_SCHEMA = "Holiday"

/** Every schema whose times the year draws. */
export const YEAR_SCHEMAS = [
  ...HOSTED_EVENT_SCHEMAS,
  CONFERENCE_SCHEMA,
  TRAVEL_SCHEMA,
  HOLIDAY_SCHEMA,
]

export type YearGroup = "programs" | "conferences" | "travel" | "holidays"

/** The row groups, in the order they stack. */
export const YEAR_GROUPS: Array<{ value: YearGroup; label: string }> = [
  { value: "programs", label: "Our programs" },
  { value: "conferences", label: "Conferences" },
  { value: "travel", label: "Portfolio travel" },
  { value: "holidays", label: "Holidays" },
]

export type YearItemKind = "program" | "conference" | "deadline" | "travel" | "holiday"

export const DEADLINE_KEYS = ["cfp_deadline", "sponsor_deadline"] as const
export type DeadlineKey = (typeof DEADLINE_KEYS)[number]

const DEADLINE_LABELS: Record<DeadlineKey, string> = {
  cfp_deadline: "CFP closes",
  sponsor_deadline: "Sponsor deadline",
}

const DEADLINE_KINDS: Record<DeadlineKey, string> = {
  cfp_deadline: "CFP",
  sponsor_deadline: "Sponsor",
}

/** A deadline's short kind ("CFP", "Sponsor") from its time's `schema_key`. */
export const deadlineKind = (key: string | null | undefined): string =>
  DEADLINE_KINDS[key as DeadlineKey] ?? "Deadline"

export interface YearRecordRef {
  id: number
  name: string
}

export interface YearRecord extends YearRecordRef {
  schema_id?: number | null
  parent_id?: number | null
  /** A holiday's `data.kind` ("public", "religious"), which names its row. */
  kind?: unknown
  links?: Array<{ schema_key: string; linked?: (YearRecordRef & { type: string }) | null }> | null
}

export interface YearTime<R extends YearRecord = YearRecord> {
  id: number
  time_type: string
  schema_key?: string | null
  title?: string | null
  start_at: string
  end_at?: string | null
  timezone?: string | null
  /** Which run of a recurring time this is, once expanded; it keeps ids apart. */
  occurrence_index?: number
  record?: R | null
}

export interface YearProgram extends YearRecordRef {
  /** Its series' data: an ended program (`status`, see programActive) shows
   *  only when it has editions in range. */
  data?: { status?: unknown } | null
}

export interface YearRow {
  /** Unique across the timeline, e.g. `conference:151493`. */
  id: string
  group: YearGroup
  title: string
  /** The record the row is: a program series or a conference. */
  recordId: number | null
}

export interface YearItem<R extends YearRecord = YearRecord> {
  /** Unique per bar or marker. */
  id: string
  rowId: string
  kind: YearItemKind
  title: string
  /** Its days as local dates, `end` exclusive: `2026-11-09`–`2026-11-14`. */
  start: string
  end: string
  record: R
  deadline?: DeadlineKey
  /** The companies it's about: a trip's company, a conference's portcos going. */
  companies: Array<YearRecordRef>
}

// --- Days -----------------------------------------------------------------------

const DAY_MS = 86_400_000

const YMD = { year: "numeric", month: "2-digit", day: "2-digit" } as const

/** `YYYY-MM-DD` in `timeZone`, or the local zone when it's missing or unknown. */
function localDate(at: Date, timeZone: string | null | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { ...YMD, timeZone: timeZone || undefined }).format(at)
  } catch {
    return new Intl.DateTimeFormat("en-CA", YMD).format(at)
  }
}

/** `YYYY-MM-DD` moved on `days` days. */
export function addDateDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/**
 * The days a time covers in its own timezone, `end` exclusive. An all-day
 * range is stored as midnight to 23:59 on its last day (or midnight after it);
 * either reads as running through that last day. An instant covers its day.
 */
export function timeDays(time: Pick<YearTime, "start_at" | "end_at" | "timezone">): {
  start: string
  end: string
} {
  const startAt = new Date(time.start_at)
  const start = localDate(startAt, time.timezone)
  const endAt = toDate(time.end_at)
  if (!endAt || endAt <= startAt) return { start, end: addDateDays(start, 1) }
  const last = localDate(new Date(endAt.getTime() - 1), time.timezone)
  return { start, end: addDateDays(last < start ? start : last, 1) }
}

// --- Rows -------------------------------------------------------------------------

const linked = (record: YearRecord, key: string) =>
  (record.links ?? [])
    .filter((l) => l.schema_key === key && l.linked)
    .map((l) => ({ id: l.linked!.id, name: l.linked!.name }))

const timeId = (time: YearTime) =>
  time.occurrence_index ? `time:${time.id}:${time.occurrence_index}` : `time:${time.id}`

/** A trip's label: the conference it's for, else its name without the
 *  company it's for ("Unikraft @ CloudX" → "CloudX"). */
function tripTitle(record: YearRecord): string {
  const conference = linked(record, "conference")[0]
  if (conference) return conference.name
  const at = record.name.indexOf(" @ ")
  return at >= 0 ? record.name.slice(at + 3) : record.name
}

const HOLIDAY_ROWS: Record<string, string> = {
  public: "Public holidays",
  religious: "Religious holidays",
}

export interface YearTimelineInput<R extends YearRecord> {
  programs: YearProgram[]
  times: Array<YearTime<R>>
  /** A schema's name from its id, from the caller's catalog. */
  schemaName: (schemaId: number | null | undefined) => string | null | undefined
}

/**
 * The year's rows and the bars and markers on them. Active programs keep a
 * row whether or not an edition falls in range; ended ones only with one; a conference is a row of
 * its own, its deadlines on it; travel is a row per company; holidays a row
 * per kind. Rows order alphabetically, conferences by date.
 */
export function buildYearTimeline<R extends YearRecord>(
  input: YearTimelineInput<R>
): { rows: YearRow[]; items: Array<YearItem<R>> } {
  const rows = new Map<string, YearRow>()
  const items: Array<YearItem<R>> = []
  const programIds = new Set(input.programs.map((p) => p.id))

  const row = (id: string, group: YearGroup, title: string, recordId: number | null = null) => {
    if (!rows.has(id)) rows.set(id, { id, group, title, recordId })
    return id
  }

  for (const time of input.times) {
    const record = time.record
    if (!record) continue
    const schema = input.schemaName(record.schema_id) ?? null
    const days = timeDays(time)
    const base = { record, ...days, id: timeId(time) }
    const isEvent = time.time_type === "event"

    if (schema && HOSTED_EVENT_SCHEMAS.includes(schema)) {
      if (!isEvent || !record.parent_id || !programIds.has(record.parent_id)) continue
      items.push({
        ...base,
        rowId: `program:${record.parent_id}`,
        kind: "program",
        title: time.title || record.name,
        companies: [],
      })
    } else if (schema === CONFERENCE_SCHEMA) {
      const rowId = row(`conference:${record.id}`, "conferences", record.name, record.id)
      const deadline = DEADLINE_KEYS.find((k) => k === time.schema_key)
      const companies = linked(record, "organizations")
      if (deadline && time.time_type === "instant") {
        items.push({
          ...base,
            rowId,
          kind: "deadline",
          title: DEADLINE_LABELS[deadline],
          deadline,
          companies,
        })
      } else if (isEvent) {
        items.push({
          ...base,
            rowId,
          kind: "conference",
          title: record.name,
          companies,
        })
      }
    } else if (schema === TRAVEL_SCHEMA && isEvent) {
      const companies = linked(record, "organizations")
      const owners = companies.length ? companies : [null]
      for (const company of owners) {
        const rowId = company
          ? row(`company:${company.id}`, "travel", company.name)
          : row("company:none", "travel", "Other travel")
        items.push({
          ...base,
          id: `${base.id}:${company?.id ?? "none"}`,
          rowId,
          kind: "travel",
          title: tripTitle(record),
          companies,
        })
      }
    } else if (schema === HOLIDAY_SCHEMA && isEvent) {
      const kind = typeof record.kind === "string" && record.kind ? record.kind : null
      const rowId = row(
        `holiday:${kind ?? "other"}`,
        "holidays",
        (kind && HOLIDAY_ROWS[kind]) || "Holidays"
      )
      items.push({
        ...base,
        rowId,
        kind: "holiday",
        title: time.title || record.name,
        companies: [],
      })
    }
  }

  const withItems = new Set(items.map((i) => i.rowId))
  const programRows: YearRow[] = input.programs
    .filter((p) => programActive(p.data?.status) || withItems.has(`program:${p.id}`))
    .map((p) => ({
      id: `program:${p.id}`,
      group: "programs" as const,
      title: p.name,
      recordId: p.id,
    }))

  const firstDay = new Map<string, string>()
  for (const item of items) {
    const seen = firstDay.get(item.rowId)
    if (!seen || item.start < seen) firstDay.set(item.rowId, item.start)
  }
  const byTitle = (a: YearRow, b: YearRow) => a.title.localeCompare(b.title)
  const byDate = (a: YearRow, b: YearRow) =>
    (firstDay.get(a.id) ?? "").localeCompare(firstDay.get(b.id) ?? "") || byTitle(a, b)

  const others = [...rows.values()]
  const inGroup = (group: YearGroup) => others.filter((r) => r.group === group)
  return {
    rows: [
      ...programRows.sort(byTitle),
      ...inGroup("conferences").sort(byDate),
      ...inGroup("travel").sort((a, b) =>
        a.id === "company:none" ? 1 : b.id === "company:none" ? -1 : byTitle(a, b)
      ),
      ...inGroup("holidays").sort(byTitle),
    ],
    items,
  }
}
